interface Bar {
  label: string;
  value: number; // 0..1 for correlation, or 0..0.5 for lift %
  note?: string;
  negative?: boolean;
}

function BarChart({ bars, unit }: { bars: Bar[]; unit: string }): JSX.Element {
  const max = Math.max(...bars.map((b) => Math.abs(b.value)));
  return (
    <div className="flex flex-col gap-2">
      {bars.map((b) => (
        <div key={b.label} className="flex items-center gap-3">
          <div className="w-40 shrink-0 text-sm text-text-primary text-right">{b.label}</div>
          <div className="flex-1 h-4 rounded bg-warm-dark-2 overflow-hidden">
            <div
              className="h-full rounded"
              style={{ width: `${(Math.abs(b.value) / max) * 100}%`, background: b.negative ? '#E0574A' : '#00FFD4' }}
            />
          </div>
          <div className="w-28 shrink-0 font-mono text-sm text-text-secondary">
            {b.negative ? '−' : ''}
            {unit === 'r' ? Math.abs(b.value).toFixed(2) : `${Math.round(b.value * 100)}%`}
            {b.note ? ` ${b.note}` : ''}
          </div>
        </div>
      ))}
    </div>
  );
}

const CORRELATION: Bar[] = [
  { label: 'YouTube brand mentions', value: 0.74 },
  { label: 'Branded web mentions', value: 0.69 },
  { label: 'Branded anchor text', value: 0.57 },
  { label: 'Branded search volume', value: 0.41 },
  { label: 'Domain Rating', value: 0.3 },
  { label: 'Raw backlinks', value: 0.2 },
];

const CAUSAL: Bar[] = [
  { label: 'Add quotations', value: 0.41 },
  { label: 'Add statistics', value: 0.33 },
  { label: 'Fluent writing', value: 0.29 },
  { label: 'Cite your sources', value: 0.28 },
  { label: 'Keyword stuffing', value: 0.09, negative: true },
];

// Illustrative split of what moves your odds of being cited (from the weighting
// philosophy + research). Content is only part of it.
const CHANCE = [
  { label: 'On-page content (you control)', pct: 30, color: '#00FFD4' },
  { label: 'Entity / knowledge-base clarity', pct: 25, color: '#5BE0C4' },
  { label: 'Off-site reputation (mentions, links)', pct: 45, color: '#E0B341' },
];

function ChanceGraph(): JSX.Element {
  return (
    <div>
      <div className="flex h-6 w-full rounded overflow-hidden">
        {CHANCE.map((c) => (
          <div key={c.label} style={{ width: `${c.pct}%`, background: c.color }} title={`${c.label}: ~${c.pct}%`} />
        ))}
      </div>
      <div className="flex flex-col gap-1 mt-2">
        {CHANCE.map((c) => (
          <div key={c.label} className="flex items-center gap-2 text-sm">
            <span className="inline-block w-3 h-3 rounded" style={{ background: c.color }} />
            <span className="text-text-primary">{c.label}</span>
            <span className="font-mono text-text-secondary">~{c.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Study({ title, href, note }: { title: string; href: string; note: string }): JSX.Element {
  return (
    <li className="mb-2">
      <a href={href} target="_blank" rel="noreferrer" className="text-cyan hover:underline">
        {title} ↗
      </a>
      <span className="text-text-secondary">: {note}</span>
    </li>
  );
}

/**
 * `embedded` means this is a dashboard page, where the page head above it has
 * already given the title. On the scan page it is a section among others and
 * has to announce itself.
 */
export default function About({ embedded = false }: { embedded?: boolean } = {}): JSX.Element {
  return (
    <div className={`w-full max-w-2xl flex flex-col gap-6 text-sm leading-relaxed${embedded ? '' : ' mx-auto'}`}>
      <div>
        {!embedded && <h2 className="font-serif text-2xl mb-2">How AI citation really works</h2>}
        <p className="text-text-secondary">
          Right now everyone is selling “AI visibility”. The pitch is always the same: pay us and we'll get your brand
          mentioned by ChatGPT. Most of it is hype. This tool exists to tell you the honest version: what actually
          drives whether AI names you, what you can change yourself, and what no amount of on-page work will fix.
        </p>
      </div>

      <div className="border border-[#E0574A]/30 rounded-xl p-5 bg-[#E0574A]/5">
        <h3 className="font-serif text-lg mb-2">The uncomfortable truth</h3>
        <p className="text-text-secondary">
          You can have a <span className="text-text-primary">perfectly AIO-optimised page</span> (clean schema, great
          structure, answer-first content) and still <span className="text-text-primary">never be cited</span> if no
          one else on the internet talks about you. The strongest signals AI uses are{' '}
          <span className="text-text-primary">off your own site</span>: brand mentions, links, and presence in
          knowledge bases. On-page work is necessary but not sufficient. Anyone promising guaranteed AI mentions from
          on-page tweaks alone is selling you the hype.
        </p>
      </div>

      <div id="chance">
        <h3 className="font-serif text-lg mb-1">Content is only part of your odds</h3>
        <p className="text-text-secondary text-sm mb-3">
          Your chance of being cited doesn't come from content alone. Roughly, on-page content is only about a third of
          it. The rest is entity clarity and, above all, off-site reputation you build over time. So being “absent”
          today isn't failure: it's a to-do list. (Illustrative split based on the research below.)
        </p>
        <ChanceGraph />
      </div>

      <div className="border border-cyan/30 rounded-xl p-5 bg-cyan/5">
        <h3 className="font-serif text-lg mb-2">Don't be discouraged, and don't overpay</h3>
        <p className="text-text-secondary">
          Seeing “absent” everywhere is normal, especially for a young or niche brand. It doesn't mean you're doing
          anything wrong. It means the off-site signals aren't there <span className="text-text-primary">yet</span>.
          Everything on the <span className="text-text-primary">Fix it</span> plan is something you can do yourself, for
          free. Be sceptical of anyone charging a lot to “guarantee” AI mentions or citations. Nobody can guarantee
          that, and the levers that actually work (real content, real reputation) aren't a paid shortcut.
        </p>
      </div>

      <div>
        <h3 className="font-serif text-lg mb-1">What correlates with being cited</h3>
        <p className="text-text-secondary text-sm mb-3">
          From a 75,000-brand study. These are <span className="text-text-primary">correlations, not causation</span>.
          Big brands get mentioned and cited partly because they're big. Note how brand mentions dwarf raw backlinks,
          and all of it lives off your own site.
        </p>
        <BarChart bars={CORRELATION} unit="r" />
        <p className="text-text-secondary text-sm mt-2">correlation coefficient (r) with AI visibility · Ahrefs 75k-brand study</p>
      </div>

      <div>
        <h3 className="font-serif text-lg mb-1">What you can actually change on-page</h3>
        <p className="text-text-secondary text-sm mb-3">
          The only <span className="text-text-primary">controlled experiment</span> on content tactics (Princeton
          “GEO”). Adding quotes, statistics and sources measurably lifted how often generative engines surfaced a page.
          Keyword stuffing hurt. These are the levers this tool's Readiness score rewards.
        </p>
        <BarChart bars={CAUSAL} unit="%" />
        <p className="text-text-secondary text-sm mt-2">
          visibility lift in controlled tests · Princeton GEO paper (2023-era models, directionally trustworthy,
          magnitudes may have drifted)
        </p>
      </div>

      <div className="border border-warm-dark-2 rounded-xl p-5">
        <h3 className="font-serif text-lg mb-2">So what's your realistic chance?</h3>
        <ul className="text-text-secondary space-y-2 list-disc pl-5">
          <li>
            <span className="text-text-primary">Retrievability is a gate.</span> If AI bots can't read your page, or it
            only renders in JavaScript, your chance is ~0 regardless of content.
          </li>
          <li>
            <span className="text-text-primary">On-page gets you eligible, not chosen.</span> Good structure and clear
            entity signals make you a valid candidate. They don't, on their own, make AI pick you.
          </li>
          <li>
            <span className="text-text-primary">Off-site reputation decides it.</span> Brand mentions, links and
            knowledge-base presence are what tip AI toward naming you over a competitor. Building these is slow and
            mostly happens off your website.
          </li>
          <li>
            <span className="text-text-primary">Niche beats broad.</span> On a generic question (“best CRM”) you compete
            with giants and lose. On a specific one (“sync Shopify with Exact Online”) a focused brand can genuinely win,
            which is why this tool probes specific, product-level questions.
          </li>
        </ul>
      </div>

      <div>
        <h3 className="font-serif text-lg mb-2">The honest sources</h3>
        <ul className="text-sm pl-1">
          <Study
            title="Princeton GEO: Generative Engine Optimization"
            href="https://arxiv.org/html/2311.09735v3"
            note="the only controlled experiment on content tactics; 2023-era models"
          />
          <Study
            title="Ahrefs: AI brand visibility correlations (75k brands)"
            href="https://ahrefs.com/blog/ai-brand-visibility-correlations/"
            note="largest correlational dataset; authors stress correlation ≠ causation"
          />
          <Study
            title="seoClarity: AI Overviews & ranking overlap (362k queries)"
            href="https://www.seoclarity.net/research/aio-rankings-overlap"
            note="how much AI citations overlap with organic ranking"
          />
          <Study
            title="ppc.land: llms.txt gets ~zero AI-crawler requests"
            href="https://ppc.land/llms-txt-adoption-rises-8-8x-but-97-of-files-get-zero-ai-requests/"
            note="why this tool deliberately down-weights llms.txt hype"
          />
        </ul>
      </div>

      <p className="text-text-secondary text-sm border-t border-warm-dark-2 pt-4">
        This tool measures what it can, marks estimates as estimates, and tells you what it can't measure. Use it to
        improve your own site. Don't hand your visibility to anyone who promises magic.
      </p>
    </div>
  );
}
